import { ReactNode } from "react";
import { TextField, TextFieldProps } from "@mui/material";
import { useDebouncedDraft } from "./useDebouncedDraft";

type Props = Omit<TextFieldProps, "value" | "onChange" | "onBlur" | "helperText"> & {
  value: string;
  onChange: (value: string) => void;
  // Bump this when `value` can change out from under the field for a reason other than its own
  // typing (a record loading in, a reset elsewhere) - see useDebouncedDraft.
  resetKey?: unknown;
  // A function receives the LIVE draft (not the debounced/committed `value`) so a running
  // character counter keeps up with actual typing instead of lagging behind the debounce.
  helperText?: ReactNode | ((draft: string) => ReactNode);
};

// The useDebouncedDraft pattern (Issue 43) only isolates a keystroke from re-rendering something
// expensive elsewhere (a large line-item grid, a draft-autosave effect) if the draft's own local
// state lives in a component that ISN'T also the parent rendering that expensive thing - calling
// the hook directly inside a page component that also renders the grid re-renders the whole page
// (grid included) on every keystroke just the same, only now via the draft's setState instead of
// the page's own. This wraps the field in a separate leaf component so that never happens.
const DebouncedTextField = ({ value, onChange, resetKey, helperText, ...rest }: Props) => {
  const draft = useDebouncedDraft(value, onChange, resetKey);
  return <TextField {...rest} value={draft.draft}
    onChange={event => draft.onDraftChange(event.target.value)}
    helperText={typeof helperText === "function" ? helperText(draft.draft) : helperText}
    onBlur={draft.onBlur} />;
};

export default DebouncedTextField;
